/**
 * useResponsiveLayout Hook - Detects viewport width for responsive layout switching
 * Determines whether to render mobile or desktop layout based on 768px breakpoint
 */

import { useState, useEffect } from 'react';

/**
 * Mobile breakpoint in pixels
 * Viewport widths at or below this value trigger mobile layout
 */
export const MOBILE_BREAKPOINT = 768;

/**
 * Return type for useResponsiveLayout hook
 */
export interface UseResponsiveLayoutReturn {
  /** Whether viewport is in mobile mode (width <= 768px) */
  isMobile: boolean;
  /** Whether viewport is in desktop mode (width > 768px) */
  isDesktop: boolean;
  /** The breakpoint value used for detection */
  breakpoint: number;
}

/**
 * Detect if viewport is in mobile mode
 * @returns true if viewport width is at or below mobile breakpoint
 */
function detectMobileLayout(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

/**
 * Custom hook for responsive layout detection based on viewport width
 * 
 * Monitors viewport width and determines whether to render mobile or desktop layout.
 * Uses 768px as the breakpoint - mobile layout activates at or below this width.
 * 
 * @returns Layout detection state with isMobile, isDesktop, and breakpoint value
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, isDesktop } = useResponsiveLayout();
 *   
 *   return isMobile ? <MobileLayout /> : <DesktopLayout />;
 * }
 * ```
 */
export function useResponsiveLayout(): UseResponsiveLayoutReturn {
  const [isMobile, setIsMobile] = useState(() => detectMobileLayout());

  useEffect(() => {
    /**
     * Handle window resize events
     * Updates layout mode based on new viewport width
     */
    const handleResize = () => {
      setIsMobile(detectMobileLayout());
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    isMobile,
    isDesktop: !isMobile,
    breakpoint: MOBILE_BREAKPOINT,
  };
}
